import { type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { ThemeProvider } from "styled-components"
import {
  oppositeOf,
  readStoredMode,
  storeMode,
  systemMode,
  THEME_ATTRIBUTE,
  type ThemeMode,
  watchSystemMode,
} from "./mode"
import { ThemeModeContext, type ThemeModeContextValue } from "./ThemeModeContext"
import { themeFor } from "./tokens"

interface ThemeModeProviderProps {
  readonly children: ReactNode
  /**
   * Standalone only. `main.tsx` passes this because it owns `<html>`; the
   * federated entry does not, because there the host owns the document and a
   * remote that stamps attributes on its root is a remote that fights it.
   *
   * Without it the app still themes itself completely — every surface is drawn by
   * styled-components from the theme below. What it adds is the document-level
   * layer `global.css` styles: the `<body>` fill behind the content column, and
   * the browser's own UI (scrollbars, form controls, the address bar tint) via
   * `color-scheme`.
   */
  readonly ownsDocument?: boolean
  /** Test seam. Skips both storage and the OS query so a spec can pin the mode. */
  readonly initialMode?: ThemeMode
}

/**
 * Owns the light/dark decision and supplies the matching styled-components theme.
 *
 * The stored choice and the resolved mode are two pieces of state rather than
 * one, because "no choice yet" is a real state with behaviour of its own: the app
 * follows the OS until the user touches the toggle, and stops following it the
 * moment they do. Collapsing them would mean either ignoring the OS or overriding
 * the user at sunset.
 */
export const ThemeModeProvider = ({
  children,
  ownsDocument = false,
  initialMode,
}: ThemeModeProviderProps): ReactElement => {
  /**
   * Read once, lazily. `localStorage` and `matchMedia` are both synchronous and
   * available before paint, so the first render is already in the right mode —
   * there is no flash to correct in an effect.
   */
  const [chosenMode, setChosenMode] = useState<ThemeMode | undefined>(() => initialMode ?? readStoredMode())
  const [osMode, setOsMode] = useState<ThemeMode>(() => initialMode ?? systemMode())

  const mode = chosenMode ?? osMode
  const theme = useMemo(() => themeFor(mode), [mode])

  /**
   * Subscribed to even while a choice is in force: the listener only updates the
   * OS mode, which `mode` ignores until the user clears their choice. Dropping
   * the subscription instead would leave `osMode` stale for anyone who does.
   */
  useEffect(() => watchSystemMode(setOsMode), [])

  useEffect(() => {
    if (!ownsDocument) {
      return
    }

    document.documentElement.setAttribute(THEME_ATTRIBUTE, mode)

    /**
     * Keeps the browser chrome — the address bar on mobile, the PWA title bar —
     * from staying white over a dark page. Declared in `index.html`; this only
     * retints it.
     */
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme.color.pageBackground)
  }, [mode, ownsDocument, theme])

  const setMode = useCallback((next: ThemeMode) => {
    setChosenMode(next)
    storeMode(next)
  }, [])

  const value = useMemo<ThemeModeContextValue>(
    () => ({
      mode,
      isExplicit: chosenMode !== undefined,
      setMode,
      toggle: () => setMode(oppositeOf(mode)),
      followSystem: () => {
        setChosenMode(undefined)
        storeMode(undefined)
      },
    }),
    [mode, chosenMode, setMode],
  )

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  )
}
